/* KERNEL SNAPSHOT (screenplay-to-blocking port, 2026-07-06)
 * Source: TimelineWebApp/src/three/characters/actorCast.ts (partial extraction + injectable-pool factory refactor) @ "Previs Pro - Web Port" (no VCS; provenance = path+date+sha)
 * sha256(source): c36e387652d84c312002e400ae4b0e48150f43e83c103cbb8353518451589069
 * Recorded in src/staging/KERNEL_PROVENANCE.ts - update it if this file is deliberately forked.
 * FORKED 2026-09-30 (CASTBLEED): castByBaseModel spreads over EVERY body of a base model by the per-character key — see KERNEL_PROVENANCE forks[]. */

/** The Avaturn "look" recipe, verbatim from the registry's `avatarRecipeJson`. */
export interface AvaturnLook {
  gender: 'male' | 'female';
  baseModelId: string;
  assets: { hair: string | null; clothing: string | null; eyewear: string | null; footwear: string | null };
  hairColor: string;
  eyeColor: string;
  skinTone: number;
}

export interface CastMember {
  /** Stable registry id (`bundledId`). */
  bundledId: string;
  gender: 'male' | 'female';
  /** Human label for the (future) picker. */
  displayName: string;
  /** Avaturn base model id (`asian_man`, `white_woman`, …). */
  baseModelId: string;
  /** GLB under `public/library/actors/`. Slice 1: a gender base; per-actor after the converter. */
  file: string;
  /** The persisted Avaturn recipe — kept for fidelity, the future customizer, and re-bake. */
  look: AvaturnLook;
}

// Ethnicity tokens recognised in a custom avatar's name/recipe (most-specific first; separators stripped).
const ETHNICITIES = ['middleeastern', 'asian', 'black', 'hispanic', 'white'];

/** Best-effort Avaturn baseModelId ("asian_man") from a free string ("avaturn_asian_man_0601…") + gender. */
export function baseModelFromHint(hint: string | undefined, gender: 'male' | 'female'): string | undefined {
  if (!hint) return undefined;
  const s = hint.toLowerCase().replace(/[-_\s]/g, '');
  const eth = ETHNICITIES.find((e) => s.includes(e));
  return eth ? `${eth}_${gender === 'male' ? 'man' : 'woman'}` : undefined;
}

// E6 cast differentiation — natural-language ethnicity cues found in a screenplay character's free-text
// `appearance` dossier ("African-American man, mid-20s", "Latina", "Middle-Eastern"). Mapped onto the 5
// Avaturn ethnicities we ship (roughly-right, not exact). Each token's first match wins; ordered so the
// more specific phrases are tested before broad ones.
export const ETHNICITY_CUES: { token: string; re: RegExp }[] = [
  { token: 'middleeastern', re: /middle[\s-]*eastern|arab|persian|iranian|lebanese|egyptian|turkish|israeli/i },
  { token: 'hispanic', re: /hispanic|latin[oax]|latina|mexican|cuban|puerto[\s-]*rican|colombian|chicano|brazilian/i },
  { token: 'black', re: /black|african|afro|negro|jamaican|caribbean|nigerian|ethiopian|haitian/i },
  // \basian\b so "Caucasian" (contains the substring "asian") doesn't mis-match → it falls through to white.
  { token: 'asian', re: /\basian\b|chinese|japanese|korean|vietnamese|filipin[oa]|thai|\bindian\b|pakistani|south[\s-]*asian|east[\s-]*asian/i },
  { token: 'white', re: /white|caucasian|european|nordic|scandinavian|anglo|irish|german|russian|italian|caucasoid|fair[\s-]*skinned/i },
];

/** Ethnicity token ("black", "asian", …) inferred from a free-text appearance description, or undefined. */
export function ethnicityFromDescription(text: string | undefined): string | undefined {
  if (!text) return undefined;
  return ETHNICITY_CUES.find((c) => c.re.test(text))?.token;
}

/** Stable non-negative hash of a character's NAME — the distinctness key when there's no persistent
 *  characterid, so two differently-named characters of the same gender don't collide onto one face. */
function nameHash(name: string | undefined): number | undefined {
  if (!name) return undefined;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Female unless the record clearly reads male — defaults to male (legacy default body). */
function genderOf(gendertype: string): 'male' | 'female' {
  return /female/i.test(gendertype) ? 'female' : 'male';
}

/** Trailing digits of a deprecated `charactertype` (`Male01`…), used only as a fallback key. */
function legacyVariantIndex(charactertype: string): number {
  const m = /(\d+)\s*$/.exec(charactertype ?? '');
  return m ? parseInt(m[1], 10) : 0;
}

/** Map a key onto the cast pool. Plain modulo spreads sequential `characterid`s perfectly. */
function spread(key: number, mod: number): number {
  return ((key % mod) + mod) % mod;
}

// Upstream excludes its bespoke picker-only avatar (`bundledId: 'custom_ian_model'`, a.k.a. `IAN`) from
// the legacy auto-swap fallback pools by module-local object identity (`ACTOR_CAST.filter((m) => m !==
// IAN)`) — "the bespoke IAN model is picker-only, so a legacy Male01 record never silently renders as
// Ian." The pool is now caller-supplied, so identity comparison isn't available; keyed on bundledId
// instead. Verified against casting.golden.json: the fallback-spread cases (EXTRA_1..4, and the
// falls-back-to-charactertype-digits case) only reproduce byte-identical when this member is excluded
// from MALE_CAST — the golden was baked against upstream's IAN-excluded pool.
const PICKER_ONLY_BUNDLED_IDS = new Set(['custom_ian_model']);

/**
 * Build a `resolveCastForCharacter` bound to an injected cast pool (was: `ACTOR_CAST` hard-coded
 * module state). `pool` is expected to be shaped like `ACTOR_CAST` (see `casting.golden.json`'s
 * top-level `catalog`, a verbatim snapshot of it) — the resolver's behaviour over that exact pool must
 * reproduce upstream's hard-coded original byte-identical (proof: the golden replay in
 * blockingKernel.golden.test.ts).
 */
export function buildCastIndex(pool: CastMember[]) {
  // 20260930 (fork): EVERY body of a base model, in catalog order — was one member per baseModelId (last
  // wins), which made the other bundled bodies of that base unreachable by description and cast every
  // same-ethnicity, same-gender character as the one body (Airship: five characters, one black_man).
  const BY_BASEMODEL = new Map<string, CastMember[]>();
  for (const m of pool) {
    if (PICKER_ONLY_BUNDLED_IDS.has(m.bundledId)) continue;
    const group = BY_BASEMODEL.get(m.baseModelId);
    if (group) group.push(m); else BY_BASEMODEL.set(m.baseModelId, [m]);
  }
  const MALE_CAST = pool.filter((m) => m.gender === 'male' && !PICKER_ONLY_BUNDLED_IDS.has(m.bundledId));
  const FEMALE_CAST = pool.filter((m) => m.gender === 'female');

  /** Cast member whose Avaturn base model matches exactly (e.g. "asian_man" → Asian Male).
   *  20260930 (fork): picked from that base model's bodies by the per-character `key`, the same spread
   *  the gender-pool fallback uses — distinct characters diversify inside an ethnicity, the same
   *  character always lands on the same body. With one body per base the pick is that body. */
  const castByBaseModel = (baseModelId: string | undefined, key: number): CastMember | undefined => {
    const group = baseModelId ? BY_BASEMODEL.get(baseModelId) : undefined;
    return group ? group[spread(key, group.length)] : undefined;
  };

  /**
   * Legacy auto-swap: map a character record → a pre-baked cast member.
   *
   * Deprecated built-ins (the old UMA-style presets carry no ethnicity) map by **gender**, then vary
   * by the persistent **`characterid`** — so the SAME character stays consistent across shots, while
   * distinct characters diversify even when they share a `charactertype` (e.g. a crowd of `Female01`
   * extras no longer renders as identical clones). Falls back to the `charactertype` digits when
   * `characterid` is unset. With only 5 members per gender, >5 distinct characters of a gender must
   * reuse a face — unavoidable until the cast grows / the customizer lands. Custom/generated actors
   * (`customcharacterid > 0`, Tripo/imported) also fall back here until TripoProducer (3.3) lands — but
   * when we know the custom avatar's own ethnicity (its Avaturn `baseModelId`, or an ethnicity token in
   * its name) we substitute the MATCHING cast member instead of a random one, so an `asian_man` bake
   * renders as the Asian male cast actor, not whatever the `characterid` modulo happens to land on.
   * No legacy actor meshes are ported (memory `web-actor-v1-prebaked-avaturn`).
   */
  function resolveCastForCharacter(
    record: { gendertype: string; charactertype: string; characterid: number; customcharacterid: number; name?: string },
    appearance?: { baseModelId?: string; name?: string; description?: string },
  ): CastMember {
    const gender = genderOf(record.gendertype);
    // A STABLE per-character key so distinct characters diversify — the persistent characterid when set,
    // else a hash of the character's name (AI-blocked actors share a default charactertype, so keying on
    // that alone made a scene of new characters render identical), else the legacy charactertype digits.
    // 20260930 (fork): hoisted from step 3 so steps 1 and 2 spread over a base model's bodies with it too.
    const key = record.characterid > 0
      ? record.characterid
      : (nameHash(record.name) ?? legacyVariantIndex(record.charactertype));
    // 1. Exact ethnicity match from the custom avatar's recipe baseModelId, then a token in its name.
    const byRecipe = castByBaseModel(appearance?.baseModelId, key);
    if (byRecipe && byRecipe.gender === gender) return byRecipe;
    const byName = castByBaseModel(baseModelFromHint(appearance?.name, gender), key);
    if (byName) return byName;
    // 2. E6 — pick a character-appropriate face from the screenplay's described ethnicity, so the cast
    //    reads as roughly the right people ("this is MY scene") instead of a random body.
    const eth = ethnicityFromDescription(appearance?.description);
    const byDescription = castByBaseModel(eth ? `${eth}_${gender === 'male' ? 'man' : 'woman'}` : undefined, key);
    if (byDescription) return byDescription;
    // 3. Fallback: gender pool, varied by the stable per-character key above.
    const pool = gender === 'female' ? FEMALE_CAST : MALE_CAST;
    return pool[spread(key, pool.length)];
  }

  return { resolveCastForCharacter, ethnicityFromDescription };
}
